import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import App from "./App";
import { Auth0Provider, AppState } from "@auth0/auth0-react";
import {
  init as initSentry,
  browserTracingIntegration,
  browserProfilingIntegration,
  consoleLoggingIntegration,
  replayIntegration,
} from "@sentry/react";

const mode = import.meta.env.MODE;
const isProduction = mode === "production";

// Only report from real deployed environments; never from localdev or the
// MSW mock mode (which reuses the development mode but sets NODE_ENV=dev-msw).
const shouldInitializeSentry =
  Boolean(import.meta.env.VITE_SENTRY_DSN) &&
  ["development", "sandbox", "production"].includes(mode) &&
  process.env.NODE_ENV !== "dev-msw";

if (shouldInitializeSentry) {
  initSentry({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: mode,
    release: __APP_VERSION__,
    sendDefaultPii: false,
    integrations: [
      browserTracingIntegration(),
      browserProfilingIntegration(),
      replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],
    tracesSampleRate: isProduction ? 0.2 : 1.0,
    profilesSampleRate: isProduction ? 0.1 : 1.0,
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/development\.backend\.admin\.earthly\.org/,
      /^https:\/\/sandbox\.backend\.admin\.earthly\.org/,
      /^https:\/\/production\.backend\.admin\.earthly\.org/,
    ],
    // Keep Session Replay minimal to protect our quota: never record idle
    // sessions, only capture the buffered replay when an error fires.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    ignoreErrors: [
      /Object Not Found Matching Id.*MethodName:update.*ParamCount:/,
      /Java object is gone/,
      /^invalid origin$/,
    ],
  });
}

async function enableMocking() {
  if (process.env.NODE_ENV !== "dev-msw") {
    return;
  }
  const { worker } = await import("../mocks/browser");

  // `worker.start()` returns a Promise that resolves
  // once the Service Worker is up and ready to intercept requests.
  return worker.start();
}

enableMocking().then(() =>
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        authorizationParams={{
          redirect_uri: `${window.location.origin}/v3/reporting`,
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: "openid read:admin-panel write:admin-panel offline_access",
        }}
        cacheLocation="localstorage"
        useRefreshTokens={true}
        onRedirectCallback={(appState?: AppState) => {
          const targetUrl = appState?.returnTo || "/v3/reporting";
          window.history.replaceState({}, document.title, targetUrl);
        }}
      >
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </Auth0Provider>
    </StrictMode>,
  ),
);
